import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { lazy } from "react";
import "../styles/global.css";
import "../styles/fonts.css";
import "@op/sf-editor/variables.css";
import { AuthProvider } from "../providers/AuthProvider";
import { RenderProvider } from "@op/utils";
import { env } from "@/utils/env";
import type { QueryClient } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { LangProvider } from "@/providers/LangProvider";
import { getSessionData } from "@/handlers/session";
import { formDevtoolsPlugin } from "@tanstack/react-form-devtools";

const TanStackDevtools = lazy(() =>
  import("@tanstack/react-devtools").then((mod) => ({
    default: mod.TanStackDevtools,
  })),
);

const TanStackRouterDevtoolsPanel = lazy(() =>
  import("@tanstack/react-router-devtools").then((mod) => ({
    default: mod.TanStackRouterDevtoolsPanel,
  })),
);

const ReactQueryDevtoolsPanel = lazy(() =>
  import("@tanstack/react-query-devtools").then((mod) => ({
    default: mod.ReactQueryDevtoolsPanel,
  })),
);

const Root = () => {
  const { sessionData } = Route.useRouteContext();

  return (
    <>
      <AuthProvider initialData={sessionData}>
        <LangProvider>
          <RenderProvider
            value={{
              context: "editor",
              renderMedium: "screen",
              apiUrl: env.VITE_API_URL,
            }}
          >
            <div className="bg-default-background w-full min-h-screen">
              <Outlet />
              <Toaster richColors />
            </div>
          </RenderProvider>
        </LangProvider>
      </AuthProvider>
      {import.meta.env.DEV && (
        <TanStackDevtools
          plugins={[
            { name: "Router", render: <TanStackRouterDevtoolsPanel /> },
            { name: "Query", render: <ReactQueryDevtoolsPanel /> },
            formDevtoolsPlugin(),
          ]}
        />
      )}
    </>
  );
};

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  beforeLoad: async () => {
    const sessionData = await getSessionData();
    return { sessionData };
  },
});
